import { graphql } from "babel-plugin-relay/macro";
import { useCallback } from "react";
import { useFragment, useLazyLoadQuery, useMutation } from "react-relay";
import { useNavigate } from "react-router-dom";
import { useCardAdvanceContentNavigation_Fragment$key } from "@generated/useCardAdvanceContentNavigation_Fragment.graphql";
import { useCardAdvanceContentNavigation_Query } from "@generated/useCardAdvanceContentNavigation_Query.graphql";
import { useCardAdvanceContentNavigation_RestartContentNodeAfterNotYetPassedMutation } from "@generated/useCardAdvanceContentNavigation_RestartContentNodeAfterNotYetPassedMutation.graphql";
import { useCardAdvanceContentNavigation_RestartContentNodeAfterPassedMutation } from "@generated/useCardAdvanceContentNavigation_RestartContentNodeAfterPassedMutation.graphql";
import { useCardAdvanceContentNavigation_StartContentNodeMutation } from "@generated/useCardAdvanceContentNavigation_StartContentNodeMutation.graphql";
import { getLearnOpportunityPath } from "../../../infecto-lms-webapp-impl/router/routes/auth/nodes.routes";
import { selectCorrectPathToContent } from "../../utils/selectCorrectPathToContent";

const QUERY = graphql`
	query useCardAdvanceContentNavigation_Query($learnOpportunityId: ID!) {
		node(id: $learnOpportunityId) {
			id
			... on LearnOpportunityV2 {
				id
				...useCardAdvanceContentNavigation_Fragment
			}
		}
	}
`;

const FRAGMENT = graphql`
	fragment useCardAdvanceContentNavigation_Fragment on LearnOpportunityV2 {
		id
		shortDescription
		root {
			id
		}
		structureDefinition {
			title
		}
		typeDefinition {
			... on LearnOpportunityELearningContentTypeDefinition {
				definitionType
				contentNodeAdvancementResult {
					status
					... on CanNotBeStartedContentNodeAdvancementResult {
						reasonConfigTypes
					}
					... on ContinueContentNodeAdvancementResult {
						activeContentSubmissionId
					}
					... on CanNotBeRestartedAfterFailedContentNodeAdvancementResult {
						status
						configResults {
							configType

							... on RestartIfFailedContentConfigResult {
								canBeRestarted
							}

							... on NegativeBlockPermanentlyWhenRewardedRestartIfFailedContentConfigResult {
								canBeRestarted
							}

							... on NegativeBlockTemporarilyWhenRewardedRestartIfFailedContentConfigResult {
								blockedUntil
							}
						}
					}
				}
				containedElementTypes
			}
		}
	}
`;

const START_CONTENT_NODE_MUTATION = graphql`
	mutation useCardAdvanceContentNavigation_StartContentNodeMutation(
		$input: StartContentNodeInput!
	) {
		LearnV2 {
			startContentNode(input: $input) {
				contentSubmission {
					id
				}
			}
		}
	}
`;

const RESTART_CONTENT_NODE_AFTER_PASSED_MUTATION = graphql`
	mutation useCardAdvanceContentNavigation_RestartContentNodeAfterPassedMutation(
		$input: RestartContentNodeAfterPassedInput!
	) {
		LearnV2 {
			restartContentNodeAfterPassed(input: $input) {
				contentSubmission {
					id
				}
			}
		}
	}
`;

const RESTART_CONTENT_NODE_AFTER_NOT_YET_PASSED_MUTATION = graphql`
	mutation useCardAdvanceContentNavigation_RestartContentNodeAfterNotYetPassedMutation(
		$input: RestartContentNodeAfterFailedInput!
	) {
		LearnV2 {
			restartContentNodeAfterFailed(input: $input) {
				contentSubmission {
					id
				}
			}
		}
	}
`;

interface OwnProps {
	contentNodeId: string;
}

export function useCardAdvanceContentNavigation({ contentNodeId }: OwnProps) {
	const navigate = useNavigate();

	const query = useLazyLoadQuery<useCardAdvanceContentNavigation_Query>(
		QUERY,
		{ learnOpportunityId: contentNodeId },
		{ fetchPolicy: "network-only" },
	);

	const learnOpportunity = useFragment<useCardAdvanceContentNavigation_Fragment$key>(
		FRAGMENT,
		query.node,
	);

	const [startContentNode] = useMutation<useCardAdvanceContentNavigation_StartContentNodeMutation>(
		START_CONTENT_NODE_MUTATION,
	);

	const [restartContentNodeAfterPassed] =
		useMutation<useCardAdvanceContentNavigation_RestartContentNodeAfterPassedMutation>(
			RESTART_CONTENT_NODE_AFTER_PASSED_MUTATION,
		);

	const [restartContentNodeAfterNotYetPassed] =
		useMutation<useCardAdvanceContentNavigation_RestartContentNodeAfterNotYetPassedMutation>(
			RESTART_CONTENT_NODE_AFTER_NOT_YET_PASSED_MUTATION,
		);

	const isNoMultipleChoiceElement =
		!learnOpportunity?.typeDefinition.containedElementTypes?.includes("multipleChoice");

	return useCallback(() => {
		const status = learnOpportunity?.typeDefinition?.contentNodeAdvancementResult?.status;

		switch (status) {
			case "CanBeStarted": {
				startContentNode({
					variables: { input: { contentNodeId } },
					onCompleted: () =>
						navigate(
							selectCorrectPathToContent(
								learnOpportunity?.root?.id,
								learnOpportunity?.id!, //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
								isNoMultipleChoiceElement,
							),
						),
				});
				break;
			}
			case "CanNotBeStarted": {
				navigate(getLearnOpportunityPath(learnOpportunity?.root?.id ?? learnOpportunity?.id ?? ""));
				break;
			}
			case "Continue": {
				navigate(
					selectCorrectPathToContent(
						learnOpportunity?.root?.id,
						learnOpportunity?.id!, //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
						isNoMultipleChoiceElement,
					),
				);
				break;
			}
			case "CanBeRestartedAfterPassed": {
				restartContentNodeAfterPassed({
					variables: { input: { contentNodeId } },
					onCompleted: () =>
						navigate(
							selectCorrectPathToContent(
								learnOpportunity?.root?.id,
								learnOpportunity?.id!, //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
								isNoMultipleChoiceElement,
							),
						),
				});
				break;
			}
			case "CanNotBeRestartedAfterPassed": {
				navigate(getLearnOpportunityPath(learnOpportunity?.root?.id ?? learnOpportunity?.id!)); //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
				break;
			}
			case "CanBeRestartedAfterFailed": {
				restartContentNodeAfterNotYetPassed({
					variables: { input: { contentNodeId } },
					onCompleted: () => navigate(getLearnOpportunityPath(contentNodeId)),
				});
				break;
			}
			case "CanNotBeRestartedAfterFailed": {
				navigate(getLearnOpportunityPath(learnOpportunity?.root?.id ?? learnOpportunity?.id!)); //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
				break;
			}
			case "BlockedByTree": {
				navigate(getLearnOpportunityPath(learnOpportunity?.root?.id ?? learnOpportunity?.id!)); //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
				break;
			}
			default: {
				navigate(getLearnOpportunityPath(learnOpportunity?.root?.id ?? learnOpportunity?.id!)); //eslint-disable-line @typescript-eslint/no-non-null-asserted-optional-chain
				break;
			}
		}
	}, [
		contentNodeId,
		learnOpportunity,
		navigate,
		startContentNode,
		restartContentNodeAfterPassed,
		restartContentNodeAfterNotYetPassed,
	]);
}
